/* ==========================================================================
   Avalanche — product listings (collection + search pages)
   <av-facets>        wraps a listing section; refreshes it over AJAX when filters,
                      sorting or pagination change and keeps the URL in sync.
   <av-price-range>   two-handle price slider bound to two number inputs.
   <av-layout-switch> grid density buttons, remembered in a cart attribute.
   ========================================================================== */

const T = () => window.Theme || {};

/* ---------------------------------------------------------------------------
   Facets
   ------------------------------------------------------------------------- */
class Facets extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.getAttribute('section-id');
    this.dirtyForms = new WeakSet();

    this.onChange = (e) => this.handleChange(e);
    this.onSubmit = (e) => this.handleSubmit(e);
    this.onClick = (e) => this.handleClick(e);
    this.onOverlayClose = (e) => this.handleOverlayClose(e);

    this.addEventListener('change', this.onChange);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);
    this.addEventListener('overlay:close', this.onOverlayClose);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('overlay:close', this.onOverlayClose);
  }

  /* --- events --- */
  handleChange(e) {
    const form = e.target.closest('form[data-facets-form]');
    if (!form || !e.target.name) return; // slider handles have no name; they forward to the number inputs
    if (form.hasAttribute('data-live')) {
      clearTimeout(this.liveTimer);
      this.liveTimer = setTimeout(() => this.apply(this.urlFromForm(form)), e.target.type === 'number' ? 400 : 0);
    } else {
      this.dirtyForms.add(form);
    }
  }

  handleSubmit(e) {
    const form = e.target.closest('form[data-facets-form]');
    if (!form) return;
    e.preventDefault();
    const overlay = form.closest('av-overlay');
    if (overlay?.isOpen) {
      // Closing the drawer applies the filters (and releases the scroll lock first)
      this.dirtyForms.add(form);
      overlay.close();
      return;
    }
    this.dirtyForms.delete(form);
    this.apply(this.urlFromForm(form));
  }

  handleClick(e) {
    const sortOption = e.target.closest('[data-sort-value]');
    if (sortOption) {
      e.preventDefault();
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortOption.dataset.sortValue);
      url.searchParams.delete('page');
      sortOption.closest('av-overlay')?.close();
      this.apply(url);
      return;
    }

    const link = e.target.closest('a[data-facet-link], .pagination a[href]');
    if (link && this.contains(link) && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      this.apply(new URL(link.href, window.location.origin), { scroll: true });
    }
  }

  handleOverlayClose(e) {
    const overlay = e.target.closest('av-overlay');
    if (!overlay || !overlay.classList.contains('facets-drawer')) return;
    const form = overlay.querySelector('form[data-facets-form]');
    if (form && this.dirtyForms.has(form)) {
      this.dirtyForms.delete(form);
      this.apply(this.urlFromForm(form));
    }
  }

  /* --- URL building --- */
  urlFromForm(form) {
    const url = new URL(form.getAttribute('action') || window.location.pathname, window.location.origin);
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (value === '' && (key === 'page' || key.startsWith('filter.v.price'))) continue;
      params.append(key, value);
    }
    url.search = params.toString();
    return url;
  }

  /* --- refresh --- */
  async apply(url, { scroll = false, push = true } = {}) {
    const { fetchSection, loadingBar, observeReveal } = T();
    const clean = new URL(url);
    clean.searchParams.delete('section_id');

    Facets.controller?.abort();
    const controller = (Facets.controller = new AbortController());

    const openState = this.readOpenState();
    this.classList.add('is-loading');
    loadingBar?.start();

    try {
      let doc;
      if (fetchSection) {
        doc = await fetchSection(clean.toString(), this.sectionId);
      } else {
        const target = new URL(clean);
        target.searchParams.set('section_id', this.sectionId);
        const res = await fetch(target, { signal: controller.signal });
        doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      }
      if (controller.signal.aborted) return;

      const fresh = doc.querySelector(`av-facets[section-id="${this.sectionId}"]`);
      if (!fresh) return;
      this.applyOpenState(fresh, openState);

      const focusedId = document.activeElement?.id;
      if (push) history.pushState({ facets: this.sectionId }, '', clean.toString());
      this.replaceChildren(...[...fresh.childNodes].map((n) => document.importNode(n, true)));
      if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
      observeReveal?.(this);

      const listing = this.querySelector('.collection');
      if (listing && (scroll || listing.getBoundingClientRect().top < 0)) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        listing.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
      }
    } catch (err) {
      if (err.name !== 'AbortError') window.location.href = clean.toString();
    } finally {
      if (Facets.controller === controller) {
        this.classList.remove('is-loading');
        loadingBar?.done();
      }
    }
  }

  /* Keep the expanded/collapsed state of filter groups across refreshes */
  readOpenState() {
    const state = new Map();
    this.querySelectorAll('form[data-facets-form] details[data-key]').forEach((d) => {
      state.set(`${d.closest('form').id}|${d.dataset.key}`, d.open);
    });
    return state;
  }

  applyOpenState(scope, state) {
    scope.querySelectorAll('form[data-facets-form] details[data-key]').forEach((d) => {
      const key = `${d.closest('form').id}|${d.dataset.key}`;
      if (state.has(key)) d.open = state.get(key);
    });
  }
}

/* Browser back/forward: re-render every listing on the page for the restored URL */
window.addEventListener('popstate', () => {
  document.querySelectorAll('av-facets').forEach((el) => el.apply?.(new URL(window.location.href), { push: false }));
});

/* ---------------------------------------------------------------------------
   Price range
   ------------------------------------------------------------------------- */
class PriceRange extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.price-range__track');
    this.low = this.querySelector('[data-handle="low"]');
    this.high = this.querySelector('[data-handle="high"]');
    this.lowField = this.querySelector('[data-field="low"]');
    this.highField = this.querySelector('[data-field="high"]');
    if (!this.low || !this.high || !this.lowField || !this.highField) return;

    this.max = Number(this.low.max) || 1;

    this.low.addEventListener('input', () => this.onSlide('low'));
    this.high.addEventListener('input', () => this.onSlide('high'));
    this.low.addEventListener('change', (e) => this.onSlideEnd(e, this.lowField));
    this.high.addEventListener('change', (e) => this.onSlideEnd(e, this.highField));

    [this.lowField, this.highField].forEach((field) => {
      field.addEventListener('focus', () => field.select());
      field.addEventListener('change', () => this.onType(field));
    });
  }

  onSlide(which) {
    const lowVal = Number(this.low.value);
    const highVal = Number(this.high.value);
    if (which === 'low' && lowVal >= highVal) this.low.value = Math.max(0, highVal - 1);
    if (which === 'high' && highVal <= lowVal) this.high.value = Math.min(this.max, lowVal + 1);
    this.lowField.value = this.low.value;
    this.highField.value = this.high.value;
    this.paint();
  }

  onSlideEnd(e, field) {
    e.stopPropagation();
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onType(field) {
    if (field.value === '') {
      (field === this.lowField ? this.low : this.high).value = field === this.lowField ? 0 : this.max;
      this.paint();
      return;
    }
    let value = Math.round(Number(field.value)) || 0;
    if (field === this.lowField) {
      const ceiling = (this.highField.value !== '' ? Number(this.highField.value) : this.max) - 1;
      value = Math.max(0, Math.min(value, ceiling));
      this.low.value = value;
    } else {
      const floor = (this.lowField.value !== '' ? Number(this.lowField.value) : 0) + 1;
      value = Math.min(this.max, Math.max(value, floor));
      this.high.value = value;
    }
    field.value = value;
    this.paint();
  }

  paint() {
    this.track.style.setProperty('--low', `${(Number(this.low.value) / this.max) * 100}%`);
    this.track.style.setProperty('--high', `${(Number(this.high.value) / this.max) * 100}%`);
  }
}

/* ---------------------------------------------------------------------------
   Grid layout switch
   ------------------------------------------------------------------------- */
class LayoutSwitch extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const button = e.target.closest('button[value]');
      if (!button || button.classList.contains('is-active')) return;
      this.select(button);
    });
  }

  select(button) {
    const device = this.getAttribute('device');
    const grid = document.getElementById(this.getAttribute('target'));
    if (grid) {
      grid.dataset[device === 'mobile' ? 'layoutMobile' : 'layoutDesktop'] = button.value;
      grid.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
    }
    this.querySelectorAll('button[value]').forEach((b) => {
      const active = b === button;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-pressed', String(active));
    });
    this.remember(device, button.value);
  }

  remember(device, value) {
    const route = T().config?.routes?.cartUpdate || '/cart/update';
    fetch(`${route}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [`products_${device}_grid_mode`]: value } }),
      keepalive: true
    }).catch(() => {});
  }
}

if (!customElements.get('av-facets')) customElements.define('av-facets', Facets);
if (!customElements.get('av-price-range')) customElements.define('av-price-range', PriceRange);
if (!customElements.get('av-layout-switch')) customElements.define('av-layout-switch', LayoutSwitch);
